import React, { useState, useEffect } from 'react';
import { MessageSquare, TrendingUp, AlertTriangle, CheckCircle, Flag, BarChart3, Target } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { fatouMonitoringService } from '../../services/fatouMonitoringService';
import ConversationLogsTable from './components/ConversationLogsTable';
import QualityControlPanel from './components/QualityControlPanel';
import ConversationAnalytics from './components/ConversationAnalytics';
import FlaggedInteractionsPanel from './components/FlaggedInteractionsPanel';
import Icon from '../../components/AppIcon';


export default function FatouAIInteractionMonitoringDashboard() {
  const [activeTab, setActiveTab] = useState('logs');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [flaggedItems, setFlaggedItems] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    isFlagged: null,
    topic: null,
    searchQuery: ''
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (activeTab === 'logs') {
      loadConversations();
    } else if (activeTab === 'flagged') {
      loadFlaggedItems();
    } else if (activeTab === 'analytics') {
      loadAnalytics();
    }
  }, [activeTab, filters]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const statsData = await fatouMonitoringService?.getConversationStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConversations = async () => {
    try {
      const data = await fatouMonitoringService?.getConversationLogs({
        ...filters,
        limit: 100
      });
      setConversations(data);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const loadFlaggedItems = async () => {
    try {
      const data = await fatouMonitoringService?.getResponseFlags({
        status: 'pending'
      });
      setFlaggedItems(data);
    } catch (error) {
      console.error('Error loading flagged items:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const endDate = new Date()?.toISOString()?.split('T')?.[0];
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
      const data = await fatouMonitoringService?.getConversationAnalytics(startDate, endDate);
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const handleFlagConversation = async (logId, flagData) => {
    try {
      await fatouMonitoringService?.flagConversation(logId, flagData);
      await loadConversations();
      await loadDashboardData();
    } catch (error) {
      console.error('Error flagging conversation:', error);
      alert('Erreur lors du signalement de la conversation');
    }
  };

  const handleUnflagConversation = async (logId) => {
    try {
      await fatouMonitoringService?.unflagConversation(logId);
      await loadConversations();
      await loadDashboardData();
    } catch (error) {
      console.error('Error unflagging conversation:', error);
      alert('Erreur lors de la suppression du signalement');
    }
  };

  const handleUpdateFlagStatus = async (flagId, status, notes) => {
    try {
      await fatouMonitoringService?.updateFlagStatus(flagId, status, notes);
      await loadFlaggedItems();
      await loadDashboardData();
    } catch (error) {
      console.error('Error updating flag status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const tabs = [
    { id: 'logs', label: 'Conversations', icon: MessageSquare, count: conversations?.length },
    { id: 'flagged', label: 'Signalements', icon: Flag, count: flaggedItems?.length },
    { id: 'quality', label: 'Contrôle Qualité', icon: Target },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
            <p className="text-gray-600">Chargement du monitoring Fatou...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Monitoring Fatou IA</h1>
          <p className="text-gray-600">Surveillance des interactions et contrôle qualité des réponses</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Conversations Totales</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.totalConversations || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <MessageSquare className="text-blue-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Messages Aujourd'hui</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.todayCount || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <TrendingUp className="text-green-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Signalements</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.flaggedCount || 0}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Confiance Moyenne</p>
                <p className="text-2xl font-bold text-gray-900">{(parseFloat(stats?.avgConfidence || 0) * 100)?.toFixed(0)}%</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <CheckCircle className="text-purple-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <div className="flex space-x-1 p-2 overflow-x-auto">
              {tabs?.map((tab) => {
                const Icon = tab?.icon;
                return (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-colors whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'bg-green-600 text-white' :'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon size={18} />
                    <span>{tab?.label}</span>
                    {tab?.count !== undefined && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        activeTab === tab?.id ? 'bg-white text-green-600' : 'bg-gray-200 text-gray-700'
                      }`}>
                        {tab?.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'logs' && (
              <ConversationLogsTable
                conversations={conversations}
                filters={filters}
                onFiltersChange={setFilters}
                onFlag={handleFlagConversation}
                onUnflag={handleUnflagConversation}
                onRefresh={loadConversations}
              />
            )}

            {activeTab === 'flagged' && (
              <FlaggedInteractionsPanel
                flaggedItems={flaggedItems}
                onUpdateStatus={handleUpdateFlagStatus}
                onRefresh={loadFlaggedItems}
              />
            )}

            {activeTab === 'quality' && (
              <QualityControlPanel
                conversations={conversations}
                onFlag={handleFlagConversation}
                onRefresh={loadConversations}
              />
            )}

            {activeTab === 'analytics' && (
              <ConversationAnalytics
                analytics={analytics}
                conversations={conversations}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}